import { Badge } from "@/components/ui/badge";
import { priorityColors, actionStatusColors } from "@/lib/task-utils";
import { formatDateDisplayString } from "@/lib/string-utils";
import { Action, ActionService } from "@/services/ActionService";
import { CodeService, type Code } from "@/services/CodeService";
import { Calendar, CheckCircle, Flag, Pencil, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import CardGridView from "../CardGridView";
import { Link, useParams } from "react-router";
import { actionEditUrlBuilder, areaIndexUrlBuilder, featureUrlBuilder, projectEditUrlBuilder, siteCreateActionUrlBuilder } from "@/lib/url-builder";
import { FeatureService } from "@/services/FeatureService";
import type { Feature } from "@/lib/models";
import { Area, AreaService } from "@/services/AreaService";
import DeleteDialog from "../DeleteDialog";
import { CacheService } from "@/services/CacheService";
import { appConfig } from "@/lib/AppConfig";
import { Loading } from "../Loading";

export interface ActionListItem {
  id: string;
  name: string;
  area: Area | null;
  priority: Code | undefined;
  status: Code | undefined;
  startDate: string | null;
  endDate: string | null;
  featureId: string | null;
  projectId: string | null;
  projectName: string | null;
  assignedUserName: string | null;
  featureName: string | null;
  attachmentUrl: string | null;
}

interface ActionCardGridViewProps {
  actionsList: Action[];
  title: string | null;
  createUrl?: string;
}

const getCode = (codeId: string, codes: Code[]): Code | undefined => {
  return codes.find((c) => c.id == codeId);
};

const mapToActionListItem = (
  action: Action,
  codes: Code[],
  featureRecord: Record<string, Feature>,
  areaRecord: Record<string, Area>,
): ActionListItem => {
  const feature = action.featureId && featureRecord[action.featureId] ? featureRecord[action.featureId] : null;
  const area = feature?.areaId && areaRecord[feature.areaId] ? areaRecord[feature.areaId] : null;

  return {
    id: action.id,
    endDate: action.endDate,
    name: action.name,
    priority: getCode(action.priorityCodeId, codes),
    startDate: action.startDate,
    status: getCode(action.statusCodeId, codes),
    featureId: action.featureId,
    area: area,
    projectId: action.projectId,
    projectName: action.projectName,
    assignedUserName: action.assignedUserName,
    featureName: action.featureName,
    attachmentUrl: action.attachmentUrl,
  };
};

const ActionCardGridView: React.FC<ActionCardGridViewProps> = ({ actionsList, title, createUrl }) => {
  const params = useParams();
  const [codes, setCodes] = useState<Code[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [deletedTaskIds, setDeletedTaskIds] = useState<Set<string>>(new Set());
  const [deletedTaskId, setDeletedTaskId] = useState<string | null>(null);
  const [showDeleteTaskDialog, setShowDeleteTaskDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [codes, features, areas] = await Promise.all([
        CacheService.get("allcodes", appConfig.resourceTypes.code, async () => {
          return CodeService.getCodes2();
        }),
        FeatureService.getBySiteId(params.siteId!), 
        AreaService.getBySiteId(params.siteId!),
      ]);
      setCodes(codes);
      setFeatures(features);
      setAreas(areas);
      setLoading(false);
    };
    fetchData();
  }, [params.siteId]);

const actionsListItems = useMemo<ActionListItem[]>(() => {
  const featureRecord: Record<string, Feature> = features.reduce(
    (acc, feature) => {
      acc[feature.id!] = feature;
      return acc;
    },
    {} as Record<string, Feature>
  );

  const areaRecord: Record<string, Area> = areas.reduce(
    (acc, area) => {
      acc[area.id!] = area;
      return acc;
    },
    {} as Record<string, Area>
  );

  return actionsList
    .filter((a) => !deletedTaskIds.has(a.id))
    .map((a) => mapToActionListItem(a, codes, featureRecord, areaRecord));
}, [actionsList, codes, features, areas, deletedTaskIds]);

  return (
    <>
      <Loading message="Loading tasks..." isLoading={loading} />
      <CardGridView
        title={title ?? "unknown"}
        editPageUrl={(_: string, id: string) =>
          actionEditUrlBuilder(params.tenantId!, params.siteId!, id)
        }
        createPageUrl={createUrl ?? siteCreateActionUrlBuilder(params.tenantId!, params.siteId!)}
        items={actionsListItems}
        getImageUrl={(task) => task.attachmentUrl}
        renderContentItem={(task) => (
          <>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-gray-400" />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority ? priorityColors[task.priority.id] : ""}`}>
                  {task.priority?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-gray-400" />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.status ? actionStatusColors[task.status.id] : ""}`}>
                  {task.status?.name}
                </span>
              </div>
       
              {task.area && <div className="flex items-center gap-2 hover:text-blue-600 transition-colors duration-200 cursor-pointer">
                <span className="flex items-center justify-between" onClick={(e) => {e.stopPropagation();}}>
                  <Link to={areaIndexUrlBuilder(params.tenantId!, params.siteId!, task.area.id!)}>{task.area.name}</Link>
                </span>
              </div>}
              {task.projectId && 
              (<div className="flex items-center gap-2 hover:text-blue-600 transition-colors duration-200 cursor-pointer"  onClick={(e) => {e.stopPropagation();}}>
                <Link to={projectEditUrlBuilder(params.tenantId!, params.siteId!, task.projectId!)}>{task.projectName}</Link>
              </div>)}
              
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-500">
                  Start: {formatDateDisplayString(task.startDate ?? "")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-500">
                  End: {formatDateDisplayString(task.endDate ?? "")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {task.assignedUserName && (
                  <Badge variant="secondary" className="text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    {task.assignedUserName}
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}
        onDeleteItem={async (task) => {
          setDeletedTaskId(task.id);
          setShowDeleteTaskDialog(true);
        }}
        tableHeaders={[
          { id: "name", child: <>Name</> },
          { id: "area", child: <>Area</> },
          { id: "project", child: <>Project</> },
          { id: "feature", child: <>Feature</> },
          {
            id: "priority",
            child: (<><Flag className="inline-block mr-1 h-4 w-4" /> Priority</>),
          },
          {
            id: "status",
            child: (<><CheckCircle className="inline-block mr-1 h-4 w-4" /> Status</>),
          },
          {
            id: "startDate",
            child: (<><Calendar className="inline-block mr-1 h-4 w-4" /> Start Date</>),
          },
          {
            id: "endDate",
            child: (<><Calendar className="inline-block mr-1 h-4 w-4" /> End Date</>),
          },
          { id: "actions", child: "Edit" },
        ]}
        tableContentItem={(task) => [
          <span key="name">
            <Link
              to={actionEditUrlBuilder(params.tenantId!, params.siteId!, task.id)}
              className="hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              key="edit"
            >
              {task.name}
            </Link>
          </span>,
          <>
            {task.area?.id && (
              <Link
                to={areaIndexUrlBuilder(params.tenantId!, params.siteId!, task.area.id)}
                className="hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              >
                {task.area?.name}
              </Link>
            )}
          </>,          
          <>
            {task.projectId && (
              <Link
                to={projectEditUrlBuilder(params.tenantId!, params.siteId!, task.projectId)}
                className="hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              >
                {task.projectName}
              </Link>
            )}
          </>,
          <>
            {task.featureId && (
              <Link
                to={featureUrlBuilder(params.tenantId!, params.siteId!, task.featureId)}
                className="hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              >
                {task.featureName}
              </Link>
            )}
          </>,          
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority ? priorityColors[task.priority.id] : ""}`}>
            {task.priority?.name}
          </span>,
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.status ? actionStatusColors[task.status.id] : ""}`}>
            {task.status?.name}
          </span>,
          <>{formatDateDisplayString(task.startDate ?? undefined)}</>,
          <>{formatDateDisplayString(task.endDate ?? undefined)}</>,
          <Link to={actionEditUrlBuilder(params.tenantId!, params.siteId!, task.id)} key="edit">
            <Pencil
              className="inline-block mr-1 h-4 w-4 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
              aria-label={`Edit ${task.name}`}
            />
          </Link>,
        ]}
      />      
      <DeleteDialog
        onDelete={async () => {
          if (!deletedTaskId) {
            alert("No task selected for deletion");
            return;
          }
          await ActionService.delete(deletedTaskId);
          setDeletedTaskIds((current) => new Set(current).add(deletedTaskId));
          setDeletedTaskId(null);
        }}
        deleteConfirmationText={
          <p>Are you sure you want to delete this task?</p>
        }
        isOpen={showDeleteTaskDialog}
        onOpenChange={setShowDeleteTaskDialog}
      />
    </>
  );
};

export default ActionCardGridView;
